import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Input, Skeleton, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { SettingsSection } from '../../pgProviderRoute/common/SettingsSection';
import { formatLogDate } from '../../pgProviderRoute/utils/formatLogDate';
import { FeatureFlagItem } from '../../../lib/api/featureFlag';
import { featureFlagQuery } from '../queries';
import { isCautionEnvironment, onOff, overrideLabel } from '../utils/featureFlagLabels';
import { FeatureFlagTable } from './FeatureFlagTable';
import { FeatureFlagSwitchModal } from './FeatureFlagSwitchModal';
import { FeatureFlagDeleteModal } from './FeatureFlagDeleteModal';
import { FeatureFlagStyle } from './styles';

type FeatureFlagPanelProps = { canUpdate: boolean };

/** 저장 직후 결과 — 알림 받은 앱이 없으면 「재시작해야 반영」을 알린다 */
type SaveResult = { label: string; notifiedInstances: number | null };

const matches = (flag: FeatureFlagItem, keyword: string): boolean => {
  const needle = keyword.trim().toLowerCase();
  if (!needle) return true;
  return [flag.ticketNumber, flag.ticketKey, flag.description, flag.memo].some((value) =>
    value?.toLowerCase().includes(needle),
  );
};

export const FeatureFlagPanel = (props: FeatureFlagPanelProps): React.JSX.Element => {
  const { canUpdate } = props;
  const query = useQuery(featureFlagQuery.list());
  const [keyword, setKeyword] = useState('');
  const [editing, setEditing] = useState<FeatureFlagItem | null>(null);
  const [deleting, setDeleting] = useState<FeatureFlagItem | null>(null);
  const [result, setResult] = useState<SaveResult | null>(null);

  // 받아 둔 목록이 있으면 새로고침이 실패해도 표를 지킨다 — 긴급 OFF 하려던 순간에 화면을 잃지 않게
  if (query.isError && !query.data)
    return (
      <Alert
        type="error"
        showIcon
        message="피처 플래그를 불러오지 못했습니다."
        action={<Button onClick={() => query.refetch()}>다시 시도</Button>}
      />
    );
  if (!query.data) return <Skeleton active paragraph={{ rows: 6 }} />;

  const { environment, flags } = query.data;
  const isProduction = isCautionEnvironment(environment);
  // 로컬 판정은 DB 값을 보지 않는다 — 서버도 쓰기를 거절한다(FEATUREFLAG010)
  const canWrite = canUpdate && environment !== 'local';
  const codeFlags = flags.filter((flag) => flag.inCode);
  const unknownFlags = flags.filter((flag) => !flag.inCode);
  const visibleFlags = codeFlags.filter((flag) => matches(flag, keyword));

  return (
    <FeatureFlagStyle>
      {query.isError && (
        <Alert
          className="settings-inline-alert"
          type="warning"
          showIcon
          message="새로고침하지 못했습니다. 아래는 마지막으로 받은 값입니다."
          action={<Button onClick={() => query.refetch()}>다시 시도</Button>}
        />
      )}
      {result &&
        (result.notifiedInstances ? (
          <Alert
            className="settings-inline-alert"
            type="success"
            showIcon
            closable
            onClose={() => setResult(null)}
            message={result.label}
            description={`앱 인스턴스 ${result.notifiedInstances}개에 알렸습니다. 몇 초 안에 반영됩니다.`}
          />
        ) : (
          <Alert
            className="settings-inline-alert"
            type="warning"
            showIcon
            closable
            onClose={() => setResult(null)}
            message={`${result.label} — 값은 저장했지만 알림을 받은 앱이 없습니다.`}
            description="FEATURE_FLAG_REDIS 설정이 빠졌거나 Redis 가 끊긴 상태입니다. 앱이 다시 연결되면 스스로 다시 읽고, 늦어도 다음 재시작 때 반영됩니다."
          />
        ))}

      <Space direction="vertical" size={20} style={{ width: '100%' }}>
        <SettingsSection
          title={`플래그 ${codeFlags.length}개`}
          description={
            isProduction
              ? '판정 = 상태값의 기본값, 덮어쓴 값이 있으면 그 값. prod 는 QA 기본값이 OFF — QA 플래그를 여기서 켜면 출시입니다.'
              : '판정 = 상태값의 기본값, 덮어쓴 값이 있으면 그 값. 「개발 중」은 덮어쓸 수 없습니다(로컬에서만 켬).'
          }
          action={
            <Space size={12} wrap>
              <Input
                aria-label="티켓 · 설명 검색"
                placeholder="티켓 · 설명 검색"
                allowClear
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                style={{ width: 240 }}
              />
              <Button icon={<ReloadOutlined />} loading={query.isFetching} onClick={() => query.refetch()}>
                새로고침
              </Button>
            </Space>
          }
        >
          <div className="flag-legend">
            <span className="flag-legend-item">
              <span className="flag-status is-IN_DEVELOPMENT">개발 중</span> 어디서도 안 켜짐(로컬만)
            </span>
            <span className="flag-legend-item">
              <span className="flag-status is-READY_FOR_QA">QA</span> 기본값 dev ON · prod OFF
            </span>
            <span className="flag-legend-item">
              <span className="flag-status is-READY_FOR_RELEASE">출시</span> 기본값 ON
            </span>
          </div>
          <FeatureFlagTable flags={visibleFlags} canWrite={canWrite} isLoading={query.isFetching} onEdit={setEditing} />
        </SettingsSection>

        {unknownFlags.length > 0 && (
          <SettingsSection
            title={`코드에 없는 값 ${unknownFlags.length}개`}
            description="코드에서 졸업했거나 오타로 남은 값입니다. 서버는 읽지 않으니 지워도 동작이 바뀌지 않습니다. 켜거나 끌 수는 없고 지우기만 됩니다."
          >
            {unknownFlags.map((flag) => (
              <div className="flag-unknown" key={flag.ticketKey}>
                <span className="flag-unknown-key">{flag.ticketKey}</span>
                <span className="flag-override">{overrideLabel(flag.dbEnabled)}</span>
                <span className="flag-unknown-meta">
                  {flag.adminName ?? '-'}
                  {flag.updateDate && ` · ${formatLogDate(flag.updateDate)}`}
                </span>
                {canWrite && (
                  <Button size="small" onClick={() => setDeleting(flag)}>
                    값 지우기
                  </Button>
                )}
              </div>
            ))}
          </SettingsSection>
        )}
      </Space>

      {editing && (
        <FeatureFlagSwitchModal
          key={editing.ticketKey}
          flag={editing}
          isProduction={isProduction}
          onClose={() => setEditing(null)}
          onSaved={(response) => {
            setEditing(null);
            setResult({
              label: `${response.flag.ticketNumber ?? response.flag.ticketKey} 저장 — 지금 ${onOff(
                response.flag.enabled,
              )} · ${overrideLabel(response.flag.dbEnabled)}`,
              notifiedInstances: response.notifiedInstances,
            });
          }}
        />
      )}
      {deleting && (
        <FeatureFlagDeleteModal
          key={deleting.ticketKey}
          flag={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(response) => {
            setDeleting(null);
            setResult({
              label: `${deleting.ticketKey} 값을 지웠습니다.`,
              notifiedInstances: response.notifiedInstances,
            });
          }}
        />
      )}
    </FeatureFlagStyle>
  );
};
